import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Info, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function PreRegistration() {
  const [searchParams] = useSearchParams();
  const initialProgram = ["football", "cheer", "7v7"].includes(searchParams.get("program")) ? searchParams.get("program") : "football";
  const [form, setForm] = useState({ child_full_name: "", age: "", grade: "", school: "", program: initialProgram, parent_phone: "", parent_guardian_email: "" });

  // Sync form.program if URL query changes (e.g. user clicks CTA in a new tab)
  useEffect(() => {
    const p = searchParams.get("program");
    if (["football", "cheer", "7v7"].includes(p)) {
      setForm((f) => (f.program === p ? f : { ...f, program: p }));
    }
  }, [searchParams]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // 7v7 is limited to ages 8-12: clamp/clear if age is outside the range
    if (k === "program" && v === "7v7") {
      const n = Number(next.age);
      if (!Number.isFinite(n) || n < 8 || n > 12) next.age = "";
    }
    return next;
  });

  const ageBounds = form.program === "7v7" ? { min: 8, max: 12 } : { min: 4, max: 18 };

  const submit = async (e) => {
    e.preventDefault();
    const ageNum = Number(form.age);
    if (form.program === "7v7" && (ageNum < 8 || ageNum > 12)) {
      toast.error("7v7 is limited to ages 8-12. Please select Football or Cheer for other ages.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/pre-registrations", { ...form, age: ageNum });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="pre-registration-page">
      <section className="bg-scu-obsidian text-white">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
          <div className="text-xs uppercase tracking-[0.35em] text-scu-electric">Early Interest</div>
          <h1 className="mt-3 font-bebas text-6xl md:text-8xl tracking-wider">Pre-Registration</h1>
          <p className="mt-4 max-w-2xl text-white/75">Help SCU gauge community interest ahead of the inaugural 2027 season.</p>
        </div>
        <div className="stripe-divider" />
      </section>

      <section className="mx-auto max-w-2xl px-4 lg:px-8 py-16">
        {done ? (
          <div className="text-center bg-white border border-scu-smoke/15 rounded-md p-10" data-testid="pre-reg-success">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h2 className="mt-4 font-bebas text-4xl tracking-wide text-scu-obsidian">Thank You!</h2>
            <p className="mt-3 text-scu-smoke">Thank you for your interest in St. Charles United. Your pre-registration helps us plan for the inaugural 2027 season and show sponsors the strength of our community.</p>
            <Button data-testid="pre-reg-another" onClick={() => { setForm({ child_full_name: "", age: "", grade: "", school: "", program: "football", parent_phone: "", parent_guardian_email: "" }); setDone(false); }} className="mt-6 bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
              Pre-Register Another Child
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-start gap-3 p-4 border border-scu-purple/30 bg-scu-purple/5 rounded-md" data-testid="pre-reg-disclaimer">
              <Info className="h-5 w-5 text-scu-purple flex-shrink-0 mt-0.5" />
              <div className="text-sm text-scu-smoke leading-relaxed">
                <b className="text-scu-obsidian">Not an official registration.</b> Signing this pre-registration is not a commitment to play for St. Charles United. Pre-registration is being used only to estimate potential participation and help SCU demonstrate community interest while pursuing additional sponsors and partnerships. Completing this form does not guarantee a roster spot and does not require the family to complete official registration later.
              </div>
            </div>

            <form onSubmit={submit} className="bg-white border border-scu-smoke/15 rounded-md p-6 md:p-8 space-y-5">
              <F label="Child's Full Name" required>
                <Input data-testid="pre-name" required value={form.child_full_name} onChange={(e) => set("child_full_name", e.target.value)} />
              </F>
              <div className="grid md:grid-cols-3 gap-5">
                <F label="Age" required>
                  <Input data-testid="pre-age" required type="number" min={ageBounds.min} max={ageBounds.max} value={form.age} onChange={(e) => set("age", e.target.value)} />
                  {form.program === "7v7" && <div className="text-[10px] uppercase tracking-widest text-scu-purple font-bold mt-1">7v7 · Ages 8-12 only</div>}
                </F>
                <F label="Grade" required><Input data-testid="pre-grade" required value={form.grade} onChange={(e) => set("grade", e.target.value)} /></F>
                <F label="School" required><Input data-testid="pre-school" required value={form.school} onChange={(e) => set("school", e.target.value)} /></F>
              </div>
              <F label="Program Interest" required>
                <RadioGroup data-testid="pre-program" value={form.program} onValueChange={(v) => set("program", v)} className="flex flex-wrap gap-6 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="football" data-testid="pre-program-football" /> Football</label>
                  <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="cheer" data-testid="pre-program-cheer" /> Cheer</label>
                  <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="7v7" data-testid="pre-program-7v7" /> 7v7 <span className="text-[10px] uppercase tracking-widest text-scu-smoke/70 ml-1">Ages 8-12</span></label>
                </RadioGroup>
              </F>
              <div className="grid md:grid-cols-2 gap-5">
                <F label="Parent/Guardian Email" required>
                  <Input data-testid="pre-email" required type="email" value={form.parent_guardian_email} onChange={(e) => set("parent_guardian_email", e.target.value)} placeholder="parent@example.com" />
                </F>
                <F label="Parent/Guardian Phone">
                  <Input data-testid="pre-phone" type="tel" value={form.parent_phone} onChange={(e) => set("parent_phone", e.target.value)} placeholder="(985) 000-0000" />
                </F>
              </div>
              <Button data-testid="pre-submit" type="submit" disabled={loading} className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest h-11 px-6">
                {loading ? "Submitting…" : "Submit Pre-Registration"}
              </Button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">
        {label} {required && <span className="text-scu-purple">*</span>}
      </Label>
      {children}
    </div>
  );
}
